import {
    getCategoryVariableColorMap,
    GetCategoryVariableColorMapResponse,
} from '@/api/dashboards/getCategoryVariableColorMap';
import { ProjectInfoResponse } from '@/api/project';
import { FormatOptions, getContributionFormatter } from '@/dashboards/utils';
import { createVariableNameMap } from '@/dashboards/utils/createVariableMap';
import { useDuckDBQuery } from '@/duckdb';
import { DashboardRule } from '@/optimizer';
import { ReactNode, useMemo } from 'react';
import { createContext, useContext } from 'use-context-selector';

type DashboardGlobalContextType = {
    project: ProjectInfoResponse['project'];
    dashboard: ProjectInfoResponse['dashboard'];
    disabledChannelsRules: DashboardRule[];
    startDate: string;
    endDate: string;
    colorMap: GetCategoryVariableColorMapResponse;
    variableNameMap: Record<string, string>;
    isFinancialKPI: boolean;
    contributionFormatter: (value: number, options?: FormatOptions) => string;
    showWelcomeModal?: () => void;
    isInputDuckDBAvailable: boolean;
};

const DashboardGlobalContext = createContext<DashboardGlobalContextType>(
    null as unknown as DashboardGlobalContextType,
);

const DashboardGlobalProvider = ({
    project,
    dashboard,
    endDate,
    startDate,
    children,
    showWelcomeModal,
    isInputDuckDBAvailable,
}: Omit<DashboardGlobalContextType, 'colorMap' | 'variableNameMap'> & {
    children: ReactNode;
}) => {
    const { data } = useDuckDBQuery({
        queryKey: ['variable-color-map'],
        queryFn: () => getCategoryVariableColorMap(),
    });

    const variableNameMap = useMemo(
        () =>
            createVariableNameMap(
                dashboard.dashboardVariables.filter(
                    (v) => v.fileType === 'output',
                ),
            ),
        [dashboard.dashboardVariables],
    );

    const contributionFormatter = getContributionFormatter(
        project.contributionFormatting,
    );

    const isFinancialKPI: boolean =
        project.contributionFormatting === 'eur' ||
        project.contributionFormatting === 'usd';

    const disabledChannelsRules: DashboardRule[] = useMemo(() => {
        return dashboard.rules.reduce(
            (acc, rule: DashboardRule): DashboardRule[] => {
                if (
                    rule.type === 'Total budget' &&
                    rule.subtype === 'is exactly' &&
                    rule.value === '0'
                ) {
                    acc.push(rule);
                }
                return acc;
            },
            [] as DashboardRule[],
        );
    }, [dashboard.rules]);

    return (
        <DashboardGlobalContext.Provider
            value={{
                project,
                dashboard,
                endDate,
                startDate,
                disabledChannelsRules,
                contributionFormatter,
                isFinancialKPI,
                colorMap: data || {
                    variableColorMap: {},
                    categoryColorMap: {},
                },
                variableNameMap,
                showWelcomeModal,
                isInputDuckDBAvailable,
            }}
        >
            {children}
        </DashboardGlobalContext.Provider>
    );
};

const useDashboardGlobalContext = () => {
    const context = useContext(DashboardGlobalContext);

    if (!context) {
        throw new Error(
            'useDashboardGlobalContext must be used within a DashboardGlobalProvider',
        );
    }

    return context;
};

export {
    DashboardGlobalContext,
    DashboardGlobalProvider,
    useDashboardGlobalContext,
};
